import classNames from "classnames";
import {PropsWithChildren, ReactNode} from "react";

import {useCurrentUserContext} from "@/components/context/hooks";
import {Divider} from "@/components/divider/Divider.tsx";
import {useFeatureBase} from "@/components/feedback/FeatureBase";
import {FreeTrialExpired} from "@/components/freeTrialExpired/FreeTrialExpired";
import ContentContainer from "@/components/layout/ContentContainer";
import {useGTM} from "@/components/trackers/GoogleTagEvents.ts";
import {UptimeToaster} from "@/components/uptimeToaster/UptimeToaster.tsx";
import {ErrorFallback} from "@/lib/handlers/ErrorFallback";
import {UpErrorBoundary} from "@/lib/handlers/UpErrorBoundary";

import styles from "./FormContentContainer.module.scss";

export interface FormContentContainerProps extends PropsWithChildren {
  title?: ReactNode;
  actionsBar?: ReactNode;
  headerContent?: ReactNode; // For tabs or other header content
  headerClassName?: string;
  className?: string;
  variant?: "primary" | "secondary";
  tabContentClassName?: string;
}

export const FormContentContainer = ({
  title,
  actionsBar,
  headerContent,
  headerClassName,
  children,
  className,
  variant = "primary",
  tabContentClassName,
}: FormContentContainerProps) => {
  const {user} = useCurrentUserContext();
  useGTM(user);
  const {init: initFeatureBase} = useFeatureBase();
  if (user) {
    initFeatureBase(user);
  }

  return (
    <UpErrorBoundary
      fallback={
        <ContentContainer title={title}>
          <ErrorFallback />
        </ContentContainer>
      }
    >
      <div className={styles[variant]}>
        {variant === "primary" && <UptimeToaster />}
        <div className={classNames({"content-container": variant === "primary"})}>
          <div className={classNames(styles.stickyHeader, headerClassName)}>
            {(title || actionsBar) && (
              <div className={styles.header}>
                {variant === "primary" && title && (
                  <div className="pulseTextH1Bold" data-testid="heading">
                    {title}
                  </div>
                )}
                {actionsBar && <div className={styles.actionsBar}>{actionsBar}</div>}
              </div>
            )}
            {(title || actionsBar) && <Divider className={styles.divider} />}
            {headerContent && <div className={styles.tabHeader}>{headerContent}</div>}
          </div>
          <div className={classNames(styles.tabContent, tabContentClassName, className)}>
            {variant === "primary" && user!.account!.free_trial_expires_at && <FreeTrialExpired />}
            {children}
          </div>
          {actionsBar && <div className={styles.stickyFooter}>{actionsBar}</div>}
        </div>
      </div>
    </UpErrorBoundary>
  );
};
